/**
 * Sidebar contextual del módulo WhatsApp — patrón Chatwoot/Linear.
 * En cualquier ruta `/chats/*` reemplaza al Sidebar global. Usa Polaris
 * `<Navigation>` + `<Navigation.Section>`. Los filtros (bucket/channel/tag)
 * viven como query params en `/chats/inbox` (deep-linkable, share-friendly).
 */

import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Navigation } from '@/lib/polaris';
import {
  ArrowLeftIcon, ChatIcon, MentionIcon, AlertCircleIcon,
  SettingsIcon, ChartVerticalFilledIcon, PersonIcon, PersonFilledIcon,
  StarFilledIcon, LinkIcon,
} from '@/lib/polaris-icons';
import { channelIcon } from '@/components/whatsapp/wa-channel-icons';
import { ROUTES, storePath } from '@/config/routes.config';
import { useAuth } from '@smarty/shared';
import { useWaInboxNav } from '@/contexts/WaInboxNavContext';
import { useDatosCacheados } from '@/hooks/useDatosCacheados';
import { contarEscalacionesPendientes } from '@/lib/whatsapp/lasEscalaciones';
import { lineSlug } from '@/lib/conversationSlug';
import { WaInboxNavFooter } from './WaInboxNavFooter';
import { waTagColorHex } from './wa-tag-colors';

/** Buckets principales del Inbox. Orden = orden visual en el sidebar. */
const BUCKETS = [
  { value: 'all' as const, label: 'Todas', icon: ChatIcon },
  { value: 'mentions' as const, label: 'Menciones', icon: MentionIcon },
  { value: 'unattended' as const, label: 'Desatendido', icon: AlertCircleIcon },
];

/** Sub-items de Ajustes (orden = orden visual). Centralizado fuera del componente
 *  porque es config estática que no depende del state. */
const SETTINGS_SUBNAV: Array<{ route: string; label: string }> = [
  // 'Configuración' (WHATSAPP_SETTINGS) removido: la config ahora vive
  // por canal en /chats/inboxes/{id}/{section}. Ruta legacy redirige.
  { route: ROUTES.WHATSAPP_AGENTS, label: 'Agentes' },
  { route: ROUTES.WHATSAPP_TEAMS, label: 'Equipos' },
  { route: ROUTES.WHATSAPP_INBOXES, label: 'Entradas' },
  { route: ROUTES.WHATSAPP_CONECTAR, label: 'Conectar WhatsApp' },
  { route: ROUTES.WHATSAPP_TAGS, label: 'Etiquetas' },
  { route: ROUTES.WHATSAPP_CUSTOM_ATTRIBUTES, label: 'Atributos' },
  // Se llamaba «Automatización» y chocaba con el módulo Automatización: esto es a quién se asigna cada chat.
  { route: ROUTES.WHATSAPP_ASSIGNMENT_RULES, label: 'Asignación automática' },
  { route: ROUTES.WHATSAPP_REGLAS, label: 'Reglas' },
  { route: ROUTES.WHATSAPP_MACROS, label: 'Macros' },
  { route: ROUTES.WHATSAPP_CANNED_RESPONSES, label: 'Respuestas rápidas' },
  { route: ROUTES.WHATSAPP_WEBHOOKS, label: 'Integraciones' },
  { route: ROUTES.WHATSAPP_AUDIT, label: 'Auditoría' },
  { route: ROUTES.WHATSAPP_CUSTOM_ROLES, label: 'Roles' },
  { route: ROUTES.WHATSAPP_SKILLS, label: 'Skills y enrutamiento' },
  { route: ROUTES.WHATSAPP_SLA, label: 'SLA' },
];

const ADMIN_ITEMS: Array<{ route: string; label: string; icon: React.FC }> = [
  { route: ROUTES.WHATSAPP_CONTACTS, label: 'Contactos', icon: PersonFilledIcon },
  /*
   * 🚨 PLANTILLAS NO ES UN AJUSTE, Y ESTABA EN AJUSTES. La prueba que las separa no es cuán
   * seguido se usan: es si tienen un estado que cambia SIN que nadie las toque. Un ajuste se pone
   * y se queda; una plantilla la aprueba Meta, la rechaza o la RECATEGORIZA de madrugada —a
   * Vitalex le movió tres a Marketing, que es lo que define cuánto se paga por mensaje—. Y una
   * rechazada significa que un mensaje NO va a salir.
   *
   * Eso se MIRA, no se configura, y lo que se mira vive donde se trabaja. Es la misma prueba que
   * separa «Pedidos» de «Ajustes de pedidos».
   *
   * 🪤 Y HAY DOS MENÚS. `navigation.config.ts` tiene su propia lista para WhatsApp, pero en
   * `/chats/*` ESTE componente reemplaza al sidebar global: mover el ítem allá no cambia nada de
   * lo que el usuario ve. Ya pasó una vez —se movió en el archivo equivocado y el founder lo
   * cazó en la captura—. Si algo del menú de Chats no aparece, es porque se editó el otro.
   */
  // Plantillas se mudó a Automatización → Contenido (12-sep, pedido del founder): Chats es la bandeja.
  { route: ROUTES.WHATSAPP_COMENTARIOS, label: 'Comentarios', icon: MentionIcon },
  { route: ROUTES.WHATSAPP_ENTRADAS, label: 'Puertas de entrada', icon: LinkIcon },
  { route: ROUTES.WHATSAPP_CSAT, label: 'CSAT', icon: StarFilledIcon },
  { route: ROUTES.WHATSAPP_SUPERVISOR, label: 'Supervisor', icon: PersonIcon },
  { route: ROUTES.WHATSAPP_STATS, label: 'Informes', icon: ChartVerticalFilledIcon },
];

/** Formatea badges estilo Slack/Gmail: 0 → undefined, ≥100 → "99+". */
function formatBadge(n: number | undefined | null): string | undefined {
  if (!n || n <= 0) return undefined;
  return n > 99 ? '99+' : String(n);
}

/** Cache de componentes SVG por color — Polaris requiere `icon` como
 *  componente, así que precomputamos uno por color para evitar crear nuevos
 *  cada render (que causarían unmount/remount innecesario en React). */
const colorDotCache = new Map<string, React.FC>();
function getColorDot(color: string): React.FC {
  const cached = colorDotCache.get(color);
  if (cached) return cached;
  const Dot: React.FC = () => (
    <svg viewBox="0 0 20 20" width={20} height={20} aria-hidden="true" focusable="false">
      <circle cx={10} cy={10} r={5} fill={color} />
    </svg>
  );
  Dot.displayName = `TagDot(${color})`;
  colorDotCache.set(color, Dot);
  return Dot;
}

export function WaInboxNavigation() {
  const location = useLocation();
  const { storeSlug, tenantId } = useAuth();
  const {
    phoneLines, entradasDeCanal, tagCatalog, selection, setSelection,
    bucketCounts, channelCounts, tagCountsMap,
  } = useWaInboxNav();
  /*
   * §5.3 del veredicto (12-sep): el agente pidió ayuda 5 veces, las 5 vencieron y nadie las vio,
   * porque la cola no estaba en ningún menú. Va arriba de todo, con el número a la vista.
   */
  const { datos: escalacionesPendientes } = useDatosCacheados<number>({
    clave: ['escalaciones-pendientes', tenantId ?? ''],
    activo: Boolean(tenantId),
    traer: () => contarEscalacionesPendientes(tenantId as string),
  });

  // Respeta el orden manual del usuario (wa_tags.sort_order). El context
  // ya devuelve el catálogo ordenado por sort_order, pero ordenamos defensivo
  // aquí también (el catálogo es state mutable y un futuro caller podría
  // alterarlo). `name` como tiebreaker — mismo criterio que el SQL.
  const sortedTags = useMemo(
    () => [...tagCatalog].sort((a, b) => {
      const diff = (a.sort_order ?? 0) - (b.sort_order ?? 0);
      if (diff !== 0) return diff;
      return a.name.localeCompare(b.name);
    }),
    [tagCatalog],
  );

  const toUrl = (route: string) => (storeSlug ? storePath(storeSlug, route) : route);

  // URL del Inbox con query params (deep-linkable + back/forward del navegador).
  const inboxBase = toUrl(ROUTES.WHATSAPP_INBOX);
  const filterUrl = (params: Record<string, string>) => {
    const qs = new URLSearchParams(params).toString();
    return qs ? `${inboxBase}?${qs}` : inboxBase;
  };

  // El estado seleccionado del sidebar solo aplica DENTRO del Inbox. Fuera
  // (Ajustes, Agentes, etc.) Polaris resalta por match de URL.
  const isOnInbox = /\/whatsapp\/inbox/.test(location.pathname);
  const isSelected = (kind: 'bucket' | 'channel' | 'entrada' | 'tag', value: string) =>
    isOnInbox && selection.kind === kind && selection.value === value;

  const bucketItems = BUCKETS.map(({ value, label, icon }) => ({
    label,
    icon,
    url: filterUrl({ bucket: value }),
    onClick: () => setSelection({ kind: 'bucket', value }),
    selected: isSelected('bucket', value),
    badge: formatBadge(bucketCounts[value]),
  }));

  // Icono por canal: las entradas de wa_phone_lines son WhatsApp → glifo de
  // WhatsApp en vez del ícono genérico de chat (que no distinguía nada).
  const channelItems = phoneLines.map((pl) => ({
    label: pl.label || pl.phone_number || 'Línea',
    icon: channelIcon('whatsapp'),
    url: filterUrl({ channel: lineSlug(pl.display_id) ?? pl.id }),
    onClick: () => setSelection({ kind: 'channel', value: pl.id }),
    selected: isSelected('channel', pl.id),
    badge: formatBadge(channelCounts[pl.id]),
  }));

  /**
   * Las entradas que NO son WhatsApp, en la misma sección «Canales».
   *
   * 🪤 SIN ESTO LAS CONVERSACIONES DE OTROS CANALES SON INALCANZABLES. Existen y
   * salen en «Todas», pero enterradas entre cientos de conversaciones ordenadas
   * por fecha: el equipo no tiene ninguna forma de llegar a ellas. Estaban en la
   * bandeja y era como si no.
   */
  const entradaItems = entradasDeCanal.map((e) => ({
    label: e.name,
    icon: channelIcon(e.channel_type),
    url: filterUrl({ channel: e.id }),
    onClick: () => setSelection({ kind: 'entrada', value: e.id }),
    selected: isSelected('entrada', e.id),
    badge: formatBadge(channelCounts[e.id]),
  }));

  const tagItems = sortedTags.map((tag) => ({
    label: tag.name,
    icon: getColorDot(waTagColorHex(tag.color)),
    url: filterUrl({ tag: tag.name }),
    onClick: () => setSelection({ kind: 'tag', value: tag.name }),
    selected: isSelected('tag', tag.name),
    badge: formatBadge(tagCountsMap[tag.name]),
  }));

  const adminItems = [
    // «El agente pidió ayuda»: primero, y con cuántas esperan.
    { url: toUrl(ROUTES.WHATSAPP_ESCALACIONES), label: 'Escalaciones', icon: AlertCircleIcon, badge: formatBadge(escalacionesPendientes) },
    ...ADMIN_ITEMS.map(({ route, label, icon }) => ({ url: toUrl(route), label, icon })),
    {
      // Padre 'Ajustes' apunta al primer sub-item (Agentes), evitando llevar
      // a la ruta legacy WHATSAPP_SETTINGS que ahora redirige.
      url: toUrl(ROUTES.WHATSAPP_AGENTS),
      label: 'Ajustes',
      icon: SettingsIcon,
      subNavigationItems: SETTINGS_SUBNAV.map(({ route, label }) => ({
        url: toUrl(route),
        label,
      })),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <Navigation location={location.pathname}>
          <Navigation.Section
            items={[{ label: 'Volver', icon: ArrowLeftIcon, url: toUrl(ROUTES.HOME) }]}
          />
          <Navigation.Section title="Conversaciones" items={bucketItems} separator />
          {channelItems.length + entradaItems.length > 0 && (
            <Navigation.Section title="Canales" items={[...channelItems, ...entradaItems]} separator />
          )}
          {tagItems.length > 0 && (
            <Navigation.Section title="Etiquetas" items={tagItems} separator />
          )}
          <Navigation.Section title="Administración" items={adminItems} separator />
        </Navigation>
      </div>
      <WaInboxNavFooter />
    </div>
  );
}
